import t from 'react-translate';
import { useCallback, useContext, useEffect } from 'react';

// Redux
import { useAppDispatch } from 'redux/store';
import { setSaveStatus, updateMentoringProgram } from 'redux/actions/mentoring-programs';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import { wrapThunkAction } from 'redux/utils';

// Form
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';

// Components
import MatchAnnouncementDate from './match-announcement-date';
import MatchingRules from './matching-rules';

import MentoringProgramContext from '../../../context';
import moment from 'moment';
import { convertDateTime } from '../../../home/participant/session-edit-mode/session-edit-mode';
import ProgramConnections from './program-connections';

const shouldSubmitMatchAnnouncementDate = (data, matchAnnouncementDate) => {
  if (!data?.matchAnnouncementDate && matchAnnouncementDate) return true;
  const matchAnnouncementDateISO = new Date(data.matchAnnouncementDate).toISOString();
  const dataAnnouncementDateISO = new Date(matchAnnouncementDate).toISOString();
  return matchAnnouncementDateISO !== dataAnnouncementDateISO;
}

const AssignedMatchingConfiguration = () => {
  const dispatch = useAppDispatch();
  const { mentoringProgram, timeZone } = useContext(MentoringProgramContext);
  const {
    id,
    endDate,
    releasedDate,
    autoMatchingEnabled,
    matchAnnouncementDate,
    concurrentMentorConnection,
    mentorCapacityEnabled,
  } = mentoringProgram || {};
  const translationBase =
    t.MENTORING_PROGRAMS.SETTINGS.TABS.MATCHING_SETTINGS.MATCHING_TYPE
      .CONFIGURATION.ASSIGNED.MATCH_ANNOUNCEMENT_DATE;

  const validationSchema = yup.object().shape({
    maxMentees: yup.number()
          .min(1)
          .max(Infinity),
    matchAnnouncementDate: yup.string().nullable()
      .test(
        'is-after-start-date',
        translationBase.ERROR_BEFORE_RELEASE_DATE(),
        (value) => {
          if (!releasedDate || !value) return true;
          const minDate = moment(releasedDate).isAfter(moment()) ? moment(releasedDate) : moment();
          return moment(value).isAfter(minDate);
        }
      )
      .test(
        'is-before-end-date',
        translationBase.ERROR_AFTER_CLOSURE_DATE(),
        (value) => {
          if (!endDate || !value) return true;
          return new Date(value) < new Date(endDate);
        }
      ),
    mentorCapacityEnabled: yup.boolean(),
    });

    const getDefaultValues = useCallback(
      () => ({
        maxMentees: concurrentMentorConnection ?? 1,
        mentorCapacityEnabled: mentorCapacityEnabled,
        matchAnnouncementDate: null,
      }),
      [mentoringProgram]
    );


  const methods = useForm({
    mode: 'all',
    defaultValues: getDefaultValues(),
    resolver: yupResolver(validationSchema),
    shouldFocusError: false,
  });

  const { handleSubmit, reset, getValues, setValue } = methods;

  useEffect(() => {
    if (mentoringProgram) {
      reset({
        maxMentees: concurrentMentorConnection ?? 1,
        mentorCapacityEnabled: mentorCapacityEnabled,
        matchAnnouncementDate: mentoringProgram?.matchAnnouncementDate
          ? convertDateTime({
              date: mentoringProgram.matchAnnouncementDate,
              timeZone: timeZone.ianaTimeZone,
              convert: false,
            })
          : null,
      });
    }
  }, [mentoringProgram, reset]);

  const onSubmit = () => {
    const formData = getValues();
    dispatch(setSaveStatus({ newStatus: SaveStatus.IN_PROGRESS }));
    wrapThunkAction(
      dispatch(
        updateMentoringProgram({
          id,
          autoMatchingEnabled,
          matchAnnouncementDate: formData.matchAnnouncementDate,
          maxMentees: formData.maxMentees,
          mentorCapacityEnabled: formData.mentorCapacityEnabled,
        })
      )
    )
      .then(() => {
        dispatch(setSaveStatus({ newStatus: SaveStatus.COMPLETED }));
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setSaveStatus({ newStatus: null }));
        }, 3000);
      });
  };

  const onSubmitMatchAnnouncementDate = (data: { matchAnnouncementDate: string }) => {
    if (shouldSubmitMatchAnnouncementDate(data, matchAnnouncementDate)) {
      if (data.matchAnnouncementDate) {
        setValue('matchAnnouncementDate', convertDateTime({
          date: data.matchAnnouncementDate,
          timeZone: timeZone.ianaTimeZone,
        }));
        onSubmit();
      }
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <form>
          <ProgramConnections onSubmit={onSubmit} />
          <MatchAnnouncementDate
            onSubmit={handleSubmit(onSubmitMatchAnnouncementDate)}
            disabled={!releasedDate}
          />
        </form>
      </FormProvider>
      <MatchingRules />
    </>
  );
};

export default AssignedMatchingConfiguration;
